/**
 * Checkout / order review (redesigned 2026-10-17).
 *
 * Route: #/checkout/:id (id = a credit pack id or a subscription id from
 * GET /credits/pricing). Everything numeric comes from that config; the
 * client only formats and derives (discount, per-month, totals).
 *
 * - "Pay" buys through Google Play inside the Android app (billing/actions.ts);
 *   the server verifies with Google before any credit is granted. In a browser
 *   it points to the Play Store listing. Purchases are never faked.
 * - The promo bar, struck price and discount line appear ONLY when the backend
 *   sends `promo` with `percentOff`. No tax line, money-back claim or "cancel
 *   anytime" copy until billing really supports them.
 * - Payment-method tiles come from `pricing.paymentMethods`; inside the Android
 *   app Play is the only method, so they are hidden (withStorePrices).
 *
 * The baked art (frames in /img/checkout) has fixed geometry, so each frame is
 * an aspect-locked box with overlays positioned in the art's own pixel space.
 */
import { api } from '../api/client.js';
import { navigate } from '../router.js';
import { purchase, restore } from '../billing/actions.js';
import { withStorePrices } from '../billing/play.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import type {
  CreditPack,
  CreditPricingConfig,
  PaymentMethodId,
  SubscriptionTier,
} from '@loomia/shared';

const IMG = '/img/checkout';

const num = (n: number): string => n.toLocaleString('en-US');

function money(minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}

/** Overlay rect in the art's pixel space (W x H) -> percentage box. */
function box(x0: number, y0: number, x1: number, y1: number, W: number, H: number): string {
  const p = (v: number, d: number): string => `${((v / d) * 100).toFixed(3)}%`;
  return `left:${p(x0, W)};top:${p(y0, H)};width:${p(x1 - x0, W)};height:${p(y1 - y0, H)}`;
}
/** Font size in art pixels -> container-query width units. */
const fs = (px: number, W: number): string => `font-size:${((px / W) * 100).toFixed(3)}cqw`;

interface Item {
  kind: 'pack' | 'sub';
  id: string;
  title: string;
  subtitle: string;
  credits: number;
  bonusPercent: number;
  priceMinor: number;
  currency: string;
  interval?: 'weekly' | 'yearly';
  bestValue: boolean;
}

function toItem(p: CreditPricingConfig, id: string): Item | null {
  const pack: CreditPack | undefined = p.packs.find((x) => x.id === id);
  if (pack) {
    return {
      kind: 'pack',
      id,
      title: `${num(pack.credits)} Credits`,
      subtitle: pack.popular ? 'Our most popular pack' : 'One-time purchase',
      credits: pack.credits,
      bonusPercent: pack.bonusPercent,
      priceMinor: pack.priceMinorUnits,
      currency: pack.currency,
      bestValue: false,
    };
  }
  const sub: SubscriptionTier | undefined = p.subscriptions.find((x) => x.id === id);
  if (sub) {
    const yearly = sub.interval === 'yearly';
    return {
      kind: 'sub',
      id,
      title: yearly ? 'Annual Plan' : 'Weekly Plan',
      subtitle: sub.bestValue ? 'The best value for creators' : yearly ? 'Billed every year' : 'Billed every week',
      credits: sub.creditsPerPeriod,
      bonusPercent: sub.bonusPercent,
      priceMinor: sub.priceMinorUnits,
      currency: sub.currency,
      interval: sub.interval,
      bestValue: !!sub.bestValue,
    };
  }
  return null;
}

const METHODS: Record<PaymentMethodId, { ic: string; title: string; sub: string }> = {
  card: { ic: 'credit_card', title: 'Card', sub: 'Credit or Debit card' },
  wallet: { ic: 'smartphone', title: 'Mobile Wallet', sub: 'Fast and convenient' },
  local: { ic: 'account_balance', title: 'Local Payment', sub: 'Other local methods' },
};

function countdown(ms: number): [string, string, string] {
  const s = Math.max(0, Math.floor(ms / 1000));
  const p = (n: number): string => String(n).padStart(2, '0');
  // Hours are NOT capped at 24: a multi-day promo shows total hours.
  return [p(Math.floor(s / 3600)), p(Math.floor((s % 3600) / 60)), p(s % 60)];
}

export function renderCheckout(root: HTMLElement, itemId: string): void {
  let alive = true;
  let timer: number | undefined;
  let method: PaymentMethodId | null = null;
  let pricing: CreditPricingConfig | null = null;

  root.innerHTML = `
    <div class="co">
      <header class="co-head">
        <button class="co-close" type="button" id="co-close" aria-label="Close">${icon('close')}</button>
        <button class="co-restore" type="button" id="co-restore">Restore</button>
      </header>
      <div id="co-body"><p class="co-loading">Loading…</p></div>
    </div>`;

  const stop = (): void => {
    alive = false;
    if (timer !== undefined) window.clearInterval(timer);
    root.removeEventListener('click', onClick);
    mo.disconnect();
  };

  function back(): void {
    if (window.history.length > 1) window.history.back();
    else navigate('/paywall');
  }

  function onClick(e: Event): void {
    const t = e.target as HTMLElement;
    const m = t.closest<HTMLElement>('[data-method]');
    if (m?.dataset.method) {
      method = m.dataset.method as PaymentMethodId;
      root.querySelectorAll<HTMLElement>('[data-method]').forEach((el) => {
        const on = el.dataset.method === method;
        el.classList.toggle('is-on', on);
        el.setAttribute('aria-checked', String(on));
      });
      return;
    }
    if (t.closest('#co-close') || t.closest('#co-back')) back();
    else if (t.closest('#co-restore')) void restore();
    else if (t.closest('#co-pay')) {
      void purchase(itemId).then((done) => {
        if (done && alive) navigate('/home');
      });
    }
    else if (t.closest('#co-retry')) void load();
  }
  root.addEventListener('click', onClick);

  const mo = new MutationObserver(() => {
    if (!root.isConnected) stop();
  });
  mo.observe(document.body, { childList: true, subtree: true });

  const paint = (): void => {
    const host = root.querySelector<HTMLElement>('#co-body');
    const p = pricing;
    if (!alive || !host || !p) return;
    const item = toItem(p, itemId);
    if (!item) {
      host.innerHTML = `
        <div class="co-error"><p>We couldn’t find that item.</p>
        <button class="btn btn-secondary" id="co-back" type="button">Back to pricing</button></div>`;
      return;
    }

    // --- promo (only if the backend sends one that is still running) -------------------
    const promo = p.promo ?? null;
    const left = promo ? Date.parse(promo.endsAt) - Date.now() : 0;
    const pct = promo && left > 0 && promo.percentOff && promo.percentOff > 0 ? Math.min(promo.percentOff, 90) : 0;
    const promoOn = pct > 0 && promo !== null;
    const discountMinor = promoOn ? Math.round((item.priceMinor * pct) / 100) : 0;
    const totalMinor = item.priceMinor - discountMinor;
    const per = item.interval === 'weekly' ? 'week' : 'year';
    const cur = item.currency;

    const expiry =
      p.creditExpiryDays == null
        ? { ic: 'all_inclusive', t: 'No expiry', d: 'Your credits never expire' }
        : { ic: 'schedule', t: `${p.creditExpiryDays}-day validity`, d: `Credits are valid for ${p.creditExpiryDays} days` };

    const rowPool = [
      { ic: 'stars', t: `${num(item.credits)} credits included`, d: item.kind === 'sub' ? `Every ${per}` : 'Added right after payment' },
      ...(item.bonusPercent > 0 ? [{ ic: 'redeem', t: `+${item.bonusPercent}% more credits`, d: 'Per $ vs. the 300-credit pack' }] : []),
      { ic: 'auto_awesome', t: 'All AI tools', d: 'Photo, video & face swap' },
      { ic: 'verified_user', t: 'Pay on success', d: 'Failed runs are refunded' },
      expiry,
      { ic: 'toll', t: 'One balance', d: 'Credits work in every tool' },
    ].slice(0, 5);

    // --- card geometry (art 1100 x 639) -------------------------------------------------------
    const CW = 1100, CH = 639;
    const rowY = [114, 212, 309, 406, 503];
    const rows = rowPool
      .map(
        (r, i) => `
        <span class="o co-row-ic" style="${box(588, rowY[i]! + 9, 646, rowY[i]! + 73, CW, CH)}">${icon(r.ic, { fill: true })}</span>
        <span class="o co-row-tx" style="${box(664, rowY[i]!, 1008, rowY[i]! + 82, CW, CH)}">
          <b style="${fs(29, CW)}">${escapeHtml(r.t)}</b><i style="${fs(25, CW)}">${escapeHtml(r.d)}</i>
        </span>`,
      )
      .join('');

    const strike = promoOn
      ? `<s>${escapeHtml(money(item.priceMinor, cur))}</s> / ${item.kind === 'sub' ? per : 'once'}`
      : item.kind === 'sub'
        ? `Billed every ${per}`
        : 'Billed once';
    const pill2 = promoOn
      ? `−${pct}% ${escapeHtml(promo!.label)}`
      : item.bonusPercent > 0
        ? `+${item.bonusPercent}% more credits`
        : escapeHtml(expiry.t);
    const sub2 =
      item.interval === 'yearly'
        ? `That’s only ${escapeHtml(money(Math.round(totalMinor / 12), cur))} / month`
        : item.kind === 'sub'
          ? `${num(item.credits)} credits / week`
          : `${num(item.credits)} credits · one-time`;

    const card = `
      <section class="co-card co-box" style="aspect-ratio:${CW}/${CH}" aria-label="Selected ${escapeHtml(item.title)}">
        <img class="co-art" src="${IMG}/card.webp" alt="" width="${CW}" height="${CH}" decoding="async" />
        <span class="o co-pill1" style="${box(94, 80, 492, 143, CW, CH)};${fs(30, CW)}">${icon('workspace_premium', { fill: true })}${item.kind === 'sub' ? 'Selected Plan' : 'Selected Pack'}</span>
        ${item.bestValue ? `<span class="o co-badge" style="${box(800, 62, 1020, 112, CW, CH)};${fs(28, CW)}">Best Value</span>` : ''}
        <span class="o co-title" style="${box(94, 168, 500, 236, CW, CH)};${fs(64, CW)}">${escapeHtml(item.title)}</span>
        <span class="o co-subt" style="${box(94, 244, 500, 292, CW, CH)};${fs(31, CW)}">${escapeHtml(item.subtitle)}</span>
        <span class="o co-strike" style="${box(98, 346, 500, 400, CW, CH)};${fs(30, CW)}"><span>${strike}</span></span>
        <span class="o co-pill2" style="${box(90, 414, 498, 494, CW, CH)};${fs(29, CW)}">${pill2}</span>
        <span class="o co-price" style="${box(128, 498, 500, 562, CW, CH)};${fs(62, CW)}"><span>${escapeHtml(money(totalMinor, cur))}${item.kind === 'sub' ? `<small style="${fs(30, CW)}"> / ${per}</small>` : ''}</span></span>
        <span class="o co-month" style="${box(128, 568, 500, 604, CW, CH)};${fs(29, CW)}">${sub2}</span>
        ${rows}
      </section>`;

    // --- timer bar (art 1100 x 211) ---------------------------------------------------------------
    const BW = 1100, BH = 211;
    const [hh, mm, ss] = countdown(left);
    const bar = promoOn
      ? `
      <section class="co-bar co-box" style="aspect-ratio:${BW}/${BH}" aria-label="${escapeHtml(promo!.label)}">
        <img class="co-art" src="${IMG}/bar.webp" alt="" width="${BW}" height="${BH}" decoding="async" />
        <span class="o co-bar-ic" style="${box(70, 50, 180, 160, BW, BH)}">${icon('timer')}</span>
        <span class="o co-bar-tx" style="${box(190, 40, 392, 172, BW, BH)};${fs(34, BW)}">Your special offer expires in</span>
        ${[hh, mm, ss]
          .map((v, i) => {
            const x0 = [400, 509, 618][i]!;
            return `<span class="o co-digit" data-d="${i}" style="${box(x0, 64, x0 + 79, 134, BW, BH)};${fs(50, BW)}">${v}</span>
                    <span class="o co-unit" style="${box(x0, 134, x0 + 79, 166, BW, BH)};${fs(22, BW)}">${['HRS', 'MIN', 'SEC'][i]}</span>`;
          })
          .join('')}
        <span class="o co-bar-r" style="${box(742, 40, 1052, 172, BW, BH)};${fs(34, BW)}"><span>Lock in <b>${pct}% OFF</b> today!</span></span>
      </section>`
      : '';

    // --- payment tiles -----------------------------------------------------------------------------
    const methods = (p.paymentMethods ?? []).filter((m): m is PaymentMethodId => m in METHODS);
    if (!method || !methods.includes(method)) method = methods[0] ?? null;
    const tiles =
      methods.length === 0
        ? '<p class="co-empty">Paid securely with Google Play.</p>'
        : `<div class="co-tiles" role="radiogroup" aria-label="Payment method">${methods
            .map((m) => {
              const d = METHODS[m];
              return `<button class="co-tile${m === method ? ' is-on' : ''}" type="button" role="radio" aria-checked="${m === method}" data-method="${m}">
                <span class="co-tile-bg" aria-hidden="true"></span>
                <span class="co-tile-ic">${icon(d.ic)}</span>
                <b>${d.title}</b><i>${d.sub}</i>
              </button>`;
            })
            .join('')}</div>`;

    // --- order summary (art 1000 x 609) -------------------------------------------------------------
    const SW = 1000, SH = 609;
    const line2 = promoOn
      ? { l: 'Discount', m: escapeHtml(promo!.label), v: `−${escapeHtml(money(discountMinor, cur))}`, hot: true }
      : item.bonusPercent > 0
        ? // Already part of the credits above, not added on top.
          { l: 'Value', m: 'More credits per $ than the 300 pack', v: `+${item.bonusPercent}%`, hot: false }
        : { l: 'Included', m: 'Credits', v: num(item.credits), hot: false };
    const line3 = item.kind === 'sub'
      ? { l: 'Billing', m: item.interval === 'weekly' ? 'Renews every week' : 'Renews every year', v: money(item.priceMinor, cur) }
      : { l: 'Billing', m: 'One-time payment', v: '—' };
    const sumRows = [
      { l: 'Plan', m: item.kind === 'sub' ? `${escapeHtml(item.title)} (${num(item.credits)} credits)` : `Credit pack (${num(item.credits)} credits)`, v: escapeHtml(money(item.priceMinor, cur)), hot: false },
      { ...line2, hot: line2.hot },
      { ...line3, hot: false },
    ];
    const sy = [143, 234, 323];
    const summary = `
      <section class="co-sum co-box" style="aspect-ratio:${SW}/${SH}" aria-label="Order summary">
        <img class="co-art" src="${IMG}/summary.webp" alt="" width="${SW}" height="${SH}" decoding="async" />
        <span class="o co-sum-h" style="${box(52, 44, 520, 132, SW, SH)};${fs(40, SW)}">Order Summary</span>
        ${sumRows
          .map(
            (r, i) => `
          <span class="o co-sum-l" style="${box(60, sy[i]!, 270, sy[i]! + 90, SW, SH)};${fs(27, SW)}">${r.l}</span>
          <span class="o co-sum-m${r.hot ? ' is-hot' : ''}" style="${box(270, sy[i]!, 700, sy[i]! + 90, SW, SH)};${fs(27, SW)}">${r.m}</span>
          <span class="o co-sum-v${r.hot ? ' is-hot' : ''}" style="${box(690, sy[i]!, 945, sy[i]! + 90, SW, SH)};${fs(27, SW)}">${r.v}</span>`,
          )
          .join('')}
        <span class="o co-total-l" style="${box(70, 440, 480, 556, SW, SH)};${fs(44, SW)}">Total Today</span>
        <span class="o co-total-v" style="${box(480, 440, 940, 556, SW, SH)};${fs(58, SW)}">${escapeHtml(money(totalMinor, cur))}</span>
      </section>`;

    const payLabel = item.kind === 'sub' ? 'Pay Now &amp; Unlock PRO' : `Pay Now &amp; Get ${num(item.credits)} Credits`;
    const trust = [
      { ic: 'verified_user', a: 'Failed runs', b: 'are refunded' },
      promoOn
        ? { ic: 'all_inclusive', a: 'First purchase only', b: 'Limited-time offer' }
        : { ic: expiry.ic, a: expiry.t, b: expiry.d },
      item.kind === 'sub'
        ? { ic: 'bolt', a: `Renews automatically at ${money(item.priceMinor, cur)} / ${per}`, b: '' }
        : { ic: 'bolt', a: 'One-time payment', b: 'No subscription' },
    ];

    host.innerHTML = `
      <h1 class="co-title-h">Complete Your Upgrade <span class="co-pro">${icon('workspace_premium', { fill: true })}PRO</span></h1>
      <p class="co-lead">${promoOn ? `Your ${pct}% first-day offer is reserved` : 'Review your order'}</p>
      <img class="co-crown" src="${IMG}/crown.webp" alt="" width="640" height="553" decoding="async" />
      ${bar}
      ${card}
      <h2 class="co-h2">${methods.length > 1 ? 'Choose Payment Method' : 'Payment'}</h2>
      ${tiles}
      ${summary}
      <p class="co-fine">Prices shown are placeholders and will be finalized before launch.</p>
      <ul class="co-trust">${trust
        .map((t) => `<li>${icon(t.ic, { fill: true })}<span>${escapeHtml(t.a)}${t.b ? `<br>${escapeHtml(t.b)}` : ''}</span></li>`)
        .join('')}</ul>
      <div class="co-cta-wrap">
        <button class="co-cta" type="button" id="co-pay">
          <img src="${IMG}/cta.webp" alt="" width="900" height="229" decoding="async" />
          <span class="co-cta-tx">${payLabel}${icon('arrow_forward')}</span>
        </button>
      </div>`;

    if (timer !== undefined) window.clearInterval(timer);
    if (promoOn && promo) {
      const end = Date.parse(promo.endsAt);
      timer = window.setInterval(() => {
        if (!alive) return;
        const rem = end - Date.now();
        if (rem <= 0) {
          paint(); // promo over: re-derive prices without it
          return;
        }
        const parts = countdown(rem);
        root.querySelectorAll<HTMLElement>('.co-digit').forEach((el, i) => {
          el.textContent = parts[i] ?? '00';
        });
      }, 1000);
    }
  };

  const load = async (): Promise<void> => {
    try {
      pricing = await withStorePrices(await api.getPricing());
      paint();
    } catch {
      const host = root.querySelector<HTMLElement>('#co-body');
      if (!alive || !host) return;
      host.innerHTML = `
        <div class="co-error"><p>Couldn’t load your order.</p>
        <button class="btn btn-secondary" id="co-retry" type="button">Retry</button></div>`;
    }
  };
  void load();
}
